import { Outlet, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { MenuRounded } from "@mui/icons-material";
import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { ComoButton } from "../../Components/Common/ComoButton";
import color from "../../Constants/colors";
import React from "react";
import { UserRole } from "../../Enums/companyStatus";
import { useTranslation } from "react-i18next";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { hasRole } from "../../Helpers/authHelpers";
import { useUser } from "../../Query/useUsers";
import { useModuleVisitTracker } from "../../Hooks/useModuleVisitTracker";
import MaintenanceOverlay from "../Common/MaintenanceOverlay";
import Sidebar, { SIDEBAR_COLLAPSED_WIDTH, SIDEBAR_WIDTH } from "./Sidebar";
import { SidebarNavProvider } from "../../Contexts/SidebarNavContext";

const SIDEBAR_COLLAPSED_KEY = "cih_sidebar_collapsed";

const readSidebarCollapsed = (): boolean => {
  try {
    const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (stored !== null) return stored === "true";
  } catch {
    // localStorage unavailable
  }
  return window.innerWidth < 1200;
};

const saveSidebarCollapsed = (collapsed: boolean) => {
  try {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  } catch {
    // localStorage unavailable
  }
};

const Dashboard = () => {
  const { data: userData } = useUser();
  const { setColors } = useWorkspaceColors();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  useModuleVisitTracker();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const isAdmin = hasRole(UserRole.ADMIN);
  const showMaintenanceOverlay =
    userData?.maintenance_mode === true && !isAdmin;
  const { t } = useTranslation("dashboardLayout");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [path]);

  React.useEffect(() => {
    if (userData?.workspace) {
      const root = document.documentElement;

      const { primary_color, secondary_color, accent_color } =
        userData.workspace;

      if (primary_color) {
        color.primary_dark = primary_color;
        root.style.setProperty("--primary-color", primary_color);
      }

      if (secondary_color) {
        color.secondary = secondary_color;
        root.style.setProperty("--secondary-color", secondary_color);
      }

      if (accent_color) {
        color.accent = accent_color;
        root.style.setProperty("--accent-color", accent_color);
      }

      setColors({
        primary_dark: primary_color || color.primary_dark,
        secondary: secondary_color || color.secondary,
        accent: accent_color || color.accent,
      });
    }
  }, [userData, setColors]);

  const handleConfirmationDialogOpen = () => {
    setConfirmationDialogOpen(true);
  };

  const handleConfirmationDialogClose = (confirmed: boolean) => {
    if (confirmed) {
      handleLogout();
    } else {
      setConfirmationDialogOpen(false);
    }
  };

  const handleDialogClose = (_: unknown, reason: string) => {
    if (reason && (reason === "backdropClick" || reason === "escapeKeyDown")) {
      setConfirmationDialogOpen(false);
      return;
    }
    handleConfirmationDialogClose(false);
  };

  const handleLogout = async () => {
    setConfirmationDialogOpen(false);
    navigate("/logout");
  };

  const toggleSidebar = () => {
    setCollapsed((value) => {
      saveSidebarCollapsed(!value);
      return !value;
    });
  };

  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), []);

  const sidebarProps = {
    onLogout: handleConfirmationDialogOpen,
  };

  return (
    <SidebarNavProvider
      collapsed={isMobile ? false : collapsed}
      onNavigate={isMobile ? closeMobileMenu : undefined}
    >
      <Box
        sx={{
          "--sidebar-width": isMobile
            ? "0px"
            : `${collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH}px`,
          display: "flex",
          width: "100vw",
          height: "100vh",
          overflow: "hidden",
          backgroundColor: color.gray50,
        }}
      >
        <CssBaseline />
        {!isMobile && (
          <Box
            component="nav"
            sx={{ p: 1.5, pr: 0, height: "100%", flexShrink: 0 }}
          >
            <Sidebar
              collapsed={collapsed}
              onToggle={toggleSidebar}
              {...sidebarProps}
            />
          </Box>
        )}
        <Box
          component="main"
          ref={scrollRef}
          sx={{
            flexGrow: 1,
            minWidth: 0,
            height: "100%",
            overflowX: "hidden",
            overflowY: "auto",
            // index.css paints every <main> with the workspace primary colour.
            backgroundColor: `${color.gray50} !important`,
          }}
        >
          {isMobile && (
            <Box
              sx={{
                position: "sticky",
                top: 0,
                zIndex: theme.zIndex.appBar,
                height: 56,
                px: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: color.white,
                borderBottom: `1px solid ${color.gray200}`,
              }}
            >
              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t("dashboardLayout.openMenu")}
              >
                <MenuRounded sx={{ color: color.primary_dark }} />
              </IconButton>
              <Box
                component="img"
                src="/commo_logo.png"
                alt=""
                onClick={() => navigate("/home")}
                sx={{ width: 36, height: 36, cursor: "pointer" }}
              />
            </Box>
          )}
          <Outlet />
        </Box>
        {isMobile && (
          <Drawer
            variant="temporary"
            anchor="left"
            open={mobileMenuOpen}
            onClose={closeMobileMenu}
            ModalProps={{ keepMounted: true }}
            PaperProps={{
              sx: {
                width: SIDEBAR_WIDTH + 24,
                maxWidth: "90vw",
                p: 1.5,
                backgroundColor: "transparent",
                boxShadow: "none",
              },
            }}
          >
            <Sidebar
              collapsed={false}
              onToggle={closeMobileMenu}
              onNavigate={closeMobileMenu}
              {...sidebarProps}
            />
          </Drawer>
        )}
        <Dialog
          open={confirmationDialogOpen}
          onClose={handleDialogClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="alert-dialog-title">
            <Typography sx={{ color: color.primary_dark }}>
              {t("dashboardLayout.logoutConfirmationTitle")}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Typography variant="caption">
                {t("dashboardLayout.logoutConfirmationMessage")}
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <ComoButton
              secondary
              onClick={() => {
                handleConfirmationDialogClose(false);
              }}
            >
              {t("dashboardLayout.no")}
            </ComoButton>
            <ComoButton
              onClick={() => {
                handleConfirmationDialogClose(true);
              }}
              autoFocus
            >
              {t("dashboardLayout.yes")}
            </ComoButton>
          </DialogActions>
        </Dialog>
        {showMaintenanceOverlay && (
          <MaintenanceOverlay onLogout={handleConfirmationDialogOpen} />
        )}
      </Box>
    </SidebarNavProvider>
  );
};

export default Dashboard;
